import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { queryKeys } from '@/api/queries'
import { invoiceFetch as apiFetch } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { invoiceKeys } from '@/features/invoices/keys'
import { USE_MOCK_AP_INVOICES, getMockInvoice, mockVariance } from '../mock'
import type { ApInvoiceApprovalInput, ApInvoiceDuplicateDecisionInput } from '../types'
import type { ApInvoiceVariance, ApInvoiceDetail, ApInvoiceApprovalDecision } from '../view-models'
import { getApiErrorMessage } from '@/lib/api-error'

export function useCreateTaxAdjustmentDraft(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      state_codes: string[]
      reason: string
      recipient_email?: string | null
      follow_up_date?: string | null
    }) => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          thread_uuid: 'tax-thread-demo',
          message_uuid: 'tax-message-demo',
          action_uuid: 'tax-action-demo',
          certificate_count: body.state_codes.length,
          invoice_status: 'wait_for_adjustment',
          outbound_message: 'not_sent',
          payment_execution: 'not_performed',
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<Record<string, unknown>>(
        token,
        `/dashboard/ap-invoices/${uuid}/tax-adjustment/draft`,
        { method: 'POST', json: body },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(uuid),
      })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
    },
  })
}

export function useRefreshInvoiceVariance(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          id: 1,
          uuid: 'variance-demo-1',
          category: 'total',
          expected_amount: 100,
          actual_amount: 110,
          variance_amount: 10,
          requires_approval: true,
          status: 'proposed',
          tolerance_amount: 0.01,
          policy_version: '2026-07-v1',
          created_at: new Date().toISOString(),
        } satisfies ApInvoiceVariance
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceVariance>(
        token,
        `/dashboard/ap-invoices/${uuid}/variances/refresh`,
        {
          method: 'POST',
        },
      )
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(uuid),
      }),
  })
}

export function useDecideInvoiceVariance(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      varianceUuid,
      decision,
      reason,
      adjustment_category,
      recipient_email,
      follow_up_date,
    }: {
      varianceUuid: string
      decision: 'accept' | 'reject' | 'void'
      reason: string
      adjustment_category?: 'ship_to' | 'freight' | 'pricing' | 'other'
      recipient_email?: string | null
      follow_up_date?: string | null
    }) => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          ...mockVariance(uuid),
          uuid: varianceUuid,
          status: decision === 'accept' ? 'accepted' : decision === 'reject' ? 'rejected' : 'void',
          reason,
        } as ApInvoiceVariance
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceVariance>(
        token,
        `/dashboard/ap-invoices/${uuid}/variances/${varianceUuid}`,
        {
          method: 'PATCH',
          json: {
            decision,
            reason,
            adjustment_category,
            recipient_email,
            follow_up_date,
          },
        },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(uuid),
      })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
    },
  })
}

export function useDecideInvoiceApproval(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ApInvoiceApprovalInput) => {
      if (USE_MOCK_AP_INVOICES) {
        const current = getMockInvoice(uuid)
        const targetByDecision: Partial<Record<ApInvoiceApprovalDecision, string>> = {
          approve_accounting: 'payment_ready',
          approve_operations: 'needs_approval',
          approve_finance: 'payment_ready',
          request_adjustment: 'wait_for_adjustment',
          hold: 'on_hold',
          dispute: 'disputed',
          dismiss: 'dismissed',
          reopen: 'needs_approval',
        }
        return {
          ...current,
          status: targetByDecision[body.decision] ?? current.status,
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceDetail>(token, `/dashboard/ap-invoices/${uuid}/approvals`, {
        method: 'POST',
        json: body,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(uuid),
      })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.approvalCapabilities(uuid),
      })
    },
  })
}

export function useDecideInvoiceDuplicate(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ApInvoiceDuplicateDecisionInput) => {
      if (USE_MOCK_AP_INVOICES) {
        const current = getMockInvoice(uuid)
        const status =
          body.outcome === 'confirmed_duplicate'
            ? 'duplicate'
            : body.outcome === 'superseded'
              ? 'superseded'
              : current.status
        return {
          ...current,
          status,
          duplicate_status:
            body.outcome === 'not_duplicate' ? 'not_duplicate' : 'confirmed_duplicate',
          duplicate_resolution_reason: body.reason,
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoiceDetail>(token, `/dashboard/ap-invoices/${uuid}/duplicate-decision`, {
        method: 'POST',
        json: body,
      })
    },
    // Toasts live here: deciding from the item closes it, which unmounts the card that asked (USA-1628).
    onSuccess: () => {
      toast.success('Duplicate decision recorded')
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(uuid),
      })
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Duplicate decision failed')),
  })
}
